/**
 * Bookkeeping for the B2C policy-mismatch recovery (useB2CPolicies + LoginHandler).
 *
 * When the redirect hands back an account from a different B2C policy, the app clears
 * only its own cached account and starts ONE sign-in (never a logoutRedirect, which ends
 * the SSO session shared by every SkyPoint app). If the account that comes back still
 * does not match, recovery STOPS and the sign-in screen offers a manual "Sign in again"
 * instead of auto-logging-in — otherwise LoginHandler's automatic login would round-trip
 * through the still-valid SSO session, get the same account and loop silently forever.
 *
 *   "retrying" — the one automatic attempt is in flight; LoginHandler must not start another.
 *   "stopped"  — the attempt came back mismatched; LoginHandler shows the dead-end state.
 *
 * Stored in sessionStorage (per tab) with a timestamp. A marker older than
 * POLICY_MISMATCH_TTL_MS is stale and ignored, so a later, unrelated mismatch still gets
 * its one automatic retry.
 */
export const POLICY_MISMATCH_RETRY_KEY = "skyadvisor.b2cPolicyMismatchRetry";
export const POLICY_MISMATCH_TTL_MS = 5 * 60_000;

export type PolicyMismatchState = "retrying" | "stopped";

/**
 * Fired on window whenever the marker changes, so a LoginHandler that mounted BEFORE the
 * (asynchronous) redirect handling decided — e.g. a cancelled flow with no cached account —
 * still switches to the dead-end state instead of sitting on its loader.
 */
export const POLICY_MISMATCH_EVENT = "skyadvisor:b2c-policy-recovery";
const notify = () => {
  try {
    window.dispatchEvent(new Event(POLICY_MISMATCH_EVENT));
  } catch {
    /* non-browser */
  }
};

export function readPolicyMismatch(now: number = Date.now()): PolicyMismatchState | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(POLICY_MISMATCH_RETRY_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { state?: unknown; at?: unknown };
    const fresh = typeof parsed.at === "number" && now - parsed.at < POLICY_MISMATCH_TTL_MS;
    if (fresh && (parsed.state === "retrying" || parsed.state === "stopped")) {
      return parsed.state;
    }
  } catch {
    // unreadable → treat as stale
  }
  clearPolicyMismatch();
  return null;
}

export function markPolicyMismatch(state: PolicyMismatchState, now: number = Date.now()): void {
  try {
    sessionStorage.setItem(POLICY_MISMATCH_RETRY_KEY, JSON.stringify({ state, at: now }));
  } catch {
    /* storage unavailable: recovery degrades to "no marker" */
  }
  notify();
}

export function clearPolicyMismatch(): void {
  try {
    sessionStorage.removeItem(POLICY_MISMATCH_RETRY_KEY);
  } catch {
    /* ignore */
  }
  notify();
}
